import { FC } from 'react';
import styled from '@emotion/styled';
import {
  Typography,
  Spacing,
  StyledDiv,
  Button,
  CardContainer,
  SearchWithSuggestions,
  StyledFixedButtonWrapper,
  Appbar,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
  Form,
} from '@taleemabad/dsm/components';

// We define our own field interface similar to ObservationField
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { UseFormReturn } from 'react-hook-form';

export interface TransferProfileUser {
  name: string;
  designation: string;
  phone: string;
}

export interface TransferProfileSchool {
  id: string;
  name: string;
  emis: string;
}

export interface TransferProfileRegion {
  id: string;
  name: string;
  code: string;
}

export interface TransferProfileField {
  isMulti?: boolean;
  name: string;
  type: 'dropdown' | 'text' | 'number' | 'phone';
  placeholder: string;
  suggestions?: Array<{ id: string; label: string; name?: string }>;
  defaultValue?: string | number;
  disabled?: boolean;
  min?: number;
}

export interface TransferProfileProps {
  user: TransferProfileUser;
  form: UseFormReturn<any>;
  fields: TransferProfileField[];
  onTransfer: () => void;
  onCancel: () => void;
  onBack: () => void;
  isLoading?: boolean;
  showErrors?: boolean;
  profileType?: string; // Add profile type to customize the description
  disableSubmit?: boolean; // Disable submit when no effective changes
  currentProfileTypeLabelMap?: Record<string, string>; // For displaying excluded current profile type label
}

export const TransferProfile: FC<TransferProfileProps> = ({
  user,
  form,
  fields,
  onTransfer,
  onCancel,
  onBack,
  isLoading = false,
  showErrors = false,
  profileType,
  disableSubmit = false,
  currentProfileTypeLabelMap,
}) => {
  const { control, handleSubmit } = form;

  const handleFormSubmit = () => {
    onTransfer();
  };

  return (
    <StyledDiv
      flexDirection="column"
      width="100%"
      padding="0 0 var(--rk-spacing-xxxxxxxxxxl) 0"
    >
      <Appbar
        title="Transfer Profile"
        leadingIcon="arrow_back"
        onLeadingIconClick={onBack}
        data-cy="transfer-profile-appbar"
      />
      <Spacing marginTop="xl" marginBottom="xl">
        <StyledDiv flexDirection="column">
          <Typography variant="textBodyLarge">
            {profileType &&
            ['principal', 'teacher'].includes(profileType.toLowerCase())
              ? 'Transfer user profile to a different school'
              : 'Transfer user profile to a different school or region'}
          </Typography>

          <Spacing marginTop="xxxl" />

          <Typography variant="textTitleMedium">User Details</Typography>
          <Spacing marginTop="md" />

          <CardContainer
            variant="filledWithBorder"
            width="100%"
            minHeight="auto"
            data-cy="transfer-profile-user-details-card"
          >
            <StyledInfoGrid>
              <StyledInfoRow>
                <Typography variant="textLabelMedium">Name</Typography>
                <StyledCapitalizedTypography variant="textBodyMedium" data-cy="transfer-profile-user-name">
                  {user.name}
                </StyledCapitalizedTypography>
              </StyledInfoRow>
              <StyledInfoRow>
                <Typography variant="textLabelMedium">Designation</Typography>
                <StyledCapitalizedTypography variant="textBodyMedium" data-cy="transfer-profile-user-designation">
                  {user.designation}
                </StyledCapitalizedTypography>
              </StyledInfoRow>
              <StyledInfoRow>
                <Typography variant="textLabelMedium">Phone</Typography>
                <Typography variant="textBodyMedium" data-cy="transfer-profile-user-phone">{user.phone}</Typography>
              </StyledInfoRow>
            </StyledInfoGrid>
          </CardContainer>

          <Spacing marginTop="xxxl" />

          <Form {...form}>
            <form onSubmit={handleSubmit(handleFormSubmit)} noValidate data-cy="transfer-profile-form">
              <StyledDiv flexDirection="column" gap={16} marginBottom={16}>
                {fields.map((field) => (
                  <FormField
                    key={field.name}
                    control={control}
                    name={field.name}
                    render={({ field: formField, fieldState }) => (
                      <FormItem key={formField.name}>
                        <FormControl>
                          {field.type === 'dropdown' && (
                            <StyledDiv flexDirection="column" data-cy={`transfer-profile-field-${field.name}`}>
                              <Typography variant="textTitleMedium">
                                {field.placeholder}
                              </Typography>
                              <Spacing marginTop="md" />
                              <SearchWithSuggestions
                                dropdownFix={true}
                                value={(() => {
                                  if (!formField.value) return '';
                                  const match = field.suggestions?.find(
                                    (s) => s.id === formField.value,
                                  );
                                  if (match) return match.label;
                                  // Try map for excluded current type
                                  if (
                                    currentProfileTypeLabelMap &&
                                    currentProfileTypeLabelMap[formField.value]
                                  ) {
                                    return currentProfileTypeLabelMap[
                                      formField.value
                                    ];
                                  }
                                  return formField.value;
                                })()}
                                suggestions={field.suggestions ?? []}
                                placeholder={`Search ${field.placeholder.toLowerCase()}`}
                                onChange={(item) => {
                                  const val = item ? item.id : '';
                                  formField.onChange(val);
                                  form.trigger(formField.name);
                                }}
                                disabled={field.disabled}
                                showDropdownOnFocus={true}
                                preserveFullListOnPrefill={true}
                                data-cy={`transfer-profile-dropdown-${field.name}`}
                              />
                            </StyledDiv>
                          )}
                        </FormControl>
                        {showErrors && fieldState.error && <FormMessage data-cy={`transfer-profile-error-${field.name}`} />}
                      </FormItem>
                    )}
                  />
                ))}
              </StyledDiv>

              <StyledFixedButtonWrapper>
                <Button
                  type="submit"
                  variant="filled"
                  disabled={isLoading || disableSubmit}
                  data-cy="transfer-profile-submit-button"
                >
                  {isLoading ? 'Transferring...' : 'Transfer Profile'}
                </Button>
                <Spacing marginTop="md" />
                <Button
                  type="button"
                  variant="outlined"
                  onClick={onCancel}
                  disabled={isLoading}
                  data-cy="transfer-profile-cancel-button"
                >
                  Cancel
                </Button>
              </StyledFixedButtonWrapper>
            </form>
          </Form>
        </StyledDiv>
      </Spacing>
    </StyledDiv>
  );
};

const StyledInfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-lg);
  padding: var(--rk-spacing-md);
`;

const StyledInfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-xs);

  ${mq(BreakpointSizes.mobile)} {
    gap: var(--rk-spacing-xxs);
  }
`;

const StyledCapitalizedTypography = styled(Typography)`
  text-transform: capitalize;
`;
